/**
 * usePracticeStyles.ts — Extracted from PracticeView.vue Phase 4
 *
 * Contains voice style/tag management, melody watchers, transformation logic,
 * and the 15 reactive watchers that manage tag selection and voice style state.
 * This is a purely mechanical extraction with zero logic changes.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 *
 * CRITICAL: The 15 watchers must be registered in their exact original order.
 * Watcher #4 registers listeners that watcher #11 and #12 invoke.
 * Watcher #9 aggregates tags that watcher #10 reacts to.
 */
import type { AliyahType, BarMitzvahType, HaftarahType, PronunciationType, SentenceGroupType, SentenceType, TagType } from 'types/types'
import { computed, ComputedRef, nextTick, Ref, watch, watchEffect } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { findTagByName } from '@/util/Tags'
import { allTransformations, TORAH, transformationByName, TransformationType, VOWELS, VOWELS_CANTILLATIONS } from '@/util/Transformations'
import { inReadMode, inSingMode, inTextMode, SING, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ interface
export interface PracticeStylesDeps {
  // State refs (from PracticeView's toRefs)
  voiceStyleSet: Ref<VoiceStyleSet>
  selectedHiddenTag: Ref<TagType | null>
  selectedNonHiddenTags: Ref<Array<TagType>>
  automaticallyFlippedVoiceStyle: Ref<boolean>
  transformation: Ref<TransformationType | null>
  tikkunStyle: Ref<boolean>
  choosableHiddenTagsListeners: Ref<Set<() => void>>
  choosableNonHiddenTagsListeners: Ref<Set<() => void>>
  selectedBarMitzvah: Ref<BarMitzvahType | null>
  aliyah: Ref<AliyahType | null>
  haftarah: Ref<HaftarahType | null>
  sentenceGroup: Ref<SentenceGroupType | null>
  sentences: Ref<Array<SentenceType>>
  focusAnnotatedSentence: Ref<AnnotatedSentence | null>
  focusSentenceIndex: Ref<number | null>
  manualSegmentJump: Ref<boolean>

  // Computed refs
  recordingsInStyle: ComputedRef<Record<string, SentenceRecordingMetadataWithPitchedAudios[]>>
  sentence: ComputedRef<SentenceType | null>
  isRecording: ComputedRef<boolean>
  choosableHiddenTags: ComputedRef<Array<TagType>>
  choosableNonHiddenTags: ComputedRef<Array<TagType>>
  choosableTags: ComputedRef<Array<TagType>>

  // Callbacks from navigation composable
  closePracticeOverlay: () => void
  handleSentenceClick: (annotatedSentence: AnnotatedSentence, sentenceIndex: number | null) => void
  hasRecordingByAnnotatedSentence: (annotatedSentence: AnnotatedSentence) => boolean

  // Store
  store: {
    getters: {
      student: {
        barMitzvah: BarMitzvahType | null
      }
      audio: {
        getRecordingListForSentence: (sentenceId: string) => SentenceRecordingMetadataWithPitchedAudios[]
        hasRecordings: boolean
        hasRecordingsForSentence: (sentenceId: string) => boolean
      }
    }
    dispatch: {
      student: {
        clearBarMitzvah: () => void
        setBarMitzvah: (bm: BarMitzvahType) => void
      }
      audio: {
        releaseSentence: (params: { sentence: SentenceType }) => void
      }
    }
  }
}
// }}}

export function usePracticeStyles(logger: VueLogger, deps: PracticeStylesDeps) {
  // Late-binding for toggleRecord (set after Session composable is created)
  let _toggleRecord: () => void = () => {
    logger.error('toggleRecord called before wired in usePracticeStyles')
  }
  function setToggleRecord(fn: () => void) {
    _toggleRecord = fn
  }

  // {{{ autoSetTransformation
  /**
   * @invariant transformation is consistent with voice mode and content type:
   *   - haftarah/sentenceGroup: never shows Torah transformation
   *   - Sing mode + VOWELS only → auto-switch to VOWELS_CANTILLATIONS
   *   - Read mode + VOWELS_CANTILLATIONS → auto-switch to VOWELS only
   *   Non-standard transformations chosen by user are not overridden.
   * @precondition deps.transformation is not null
   * @postcondition deps.transformation may be changed if it was on a "default" value
   * @sideeffect Mutates deps.transformation
   */
  function autoSetTransformation() {
    // sentenceGroup || haftarah + torah -> vowels, can
    if (!deps.transformation.value) {
      logger.error('unexpected null state.transformation')
      return
    }

    if ((deps.haftarah.value || deps.sentenceGroup.value) && deps.transformation.value.name === transformationByName(TORAH).name) {
      deps.transformation.value = transformationByName(VOWELS_CANTILLATIONS)
    }

    // sing + vowels-only -> set to vowels, cantillations
    if (inSingMode(deps.voiceStyleSet.value) && deps.transformation.value.name === transformationByName(VOWELS).name) {
      deps.transformation.value = transformationByName(VOWELS_CANTILLATIONS)

      // read + vowels, cantillations -> set to vowels-only
    } else if (inReadMode(deps.voiceStyleSet.value) && deps.transformation.value.name === transformationByName(VOWELS_CANTILLATIONS).name) {
      deps.transformation.value = transformationByName(VOWELS)
    }
  }
  // }}}

  // {{{ computed hiddenTagsStats
  /**
   * @invariant Returns a count of recordings per hidden tag name across all current sentences
   * @depends-on deps.sentences, deps.store.getters.audio, deps.choosableHiddenTags
   */
  const hiddenTagsStats = computed<Record<string, number>>((): Record<string, number> => {
    logger.debug('hiddenTagsStats')
    const map: Record<string, number> = {}

    // count the number of read recordings and sung recordings for this sentence
    ;(deps.sentences.value as Array<SentenceType>).forEach((sentence: SentenceType) => {
      if (!deps.store.getters.audio.hasRecordingsForSentence(sentence.id)) {
        logger.debug(`hiddenTagsStats, no recordings for ${sentence.id}`)
        return
      }

      const recs = deps.store.getters.audio.getRecordingListForSentence(sentence.id)
      logger.debug(`hiddenTagsStats, recs =`)
      logger.debug(recs)
      recs.forEach((srmdwpa: SentenceRecordingMetadataWithPitchedAudios) => {
        logger.debug('hiddenTagsStats srmdwpa =')
        logger.debug(srmdwpa)
        deps.choosableHiddenTags.value.forEach((hiddenTag: TagType) => {
          if (!srmdwpa.sentenceRecording) {
            logger.error('unexpected null srmdwpa.sentenceRecording')
            return
          }

          if (findTagByName(srmdwpa.sentenceRecording.tags, hiddenTag.name)) {
            if (map[hiddenTag.name] === undefined) {
              map[hiddenTag.name] = 0
            }
            map[hiddenTag.name] += 1
          }
        })
      })
    })

    return map
  })
  // }}}
  // {{{ computed isInSingMode
  /** @depends-on deps.voiceStyleSet.voice */
  const isInSingMode = computed(() => {
    return inSingMode(deps.voiceStyleSet.value)
  })
  // }}}
  // {{{ computed isInTextMode
  /** @depends-on deps.voiceStyleSet.voice */
  const isInTextMode = computed(() => {
    return inTextMode(deps.voiceStyleSet.value)
  })
  // }}}
  // {{{ computed relevantTransformations
  /**
   * @invariant Includes Torah transformation only when an aliyah is selected
   * @depends-on deps.aliyah
   */
  const relevantTransformations = computed<Array<TransformationType>>((): Array<TransformationType> => {
    logger.info('relevantTransformations, state.aliyah =')
    logger.info(deps.aliyah.value)
    return allTransformations(!!deps.aliyah.value)
  })
  // }}}
  // {{{ computed tagWarning
  /**
   * @invariant Returns comma-separated tag names that the student must choose between to
   *   disambiguate the current selection. Empty string means no ambiguity.
   * @depends-on deps.voiceStyleSet.tags, deps.recordingsInStyle
   */
  const tagWarning = computed<string>(() => {
    if (!deps.voiceStyleSet.value || !deps.voiceStyleSet.value.tags?.length) {
      return ''
    }

    // given the current tag selection, find all sentences for which there is
    // more than 1 recording; then collect all tags for all those recordings,
    // and remove the tags that have already been selected. What remains are
    // the tags that the student needs to choose from to disambiguate the
    // current tag selection.
    const tags = new Set<TagType>()
    Object.values(deps.recordingsInStyle.value).forEach(
      (sentenceRecordingMetadataWithPitchedAudios: Array<SentenceRecordingMetadataWithPitchedAudios>) => {
        if (sentenceRecordingMetadataWithPitchedAudios.length === 1) {
          return
        }
        sentenceRecordingMetadataWithPitchedAudios.forEach(
          (sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios) => {
            if (!sentenceRecordingMetadataWithPitchedAudios.sentenceRecording) {
              return
            }

            sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.tags.forEach((tag: TagType) => {
              tags.add(tag)
            })
          },
        )
      },
    )

    deps.voiceStyleSet.value.tags.forEach((tag: TagType) => {
      tags.delete(tag)
    })

    if (!tags.size) {
      return ''
    }

    return Array.from(tags)
      .map((tag: TagType) => tag.name)
      .join(', ')
  })
  // }}}
  // {{{ computed tagWarningBox
  /**
   * @invariant True when there are no matching recordings AND choosable tags > 1 (needs disambiguation)
   * @depends-on deps.recordingsInStyle, deps.choosableTags
   */
  const tagWarningBox = computed<boolean>(() => {
    // count the number of current recordings with the current selection
    let nRecordings = 0
    logger.debug('tagWarningBox, recordingsInStyle.value =')
    logger.debug(deps.recordingsInStyle.value)
    logger.debug('tagWarningBox, Object.values(recordingsInStyle.value) =')
    logger.debug(Object.values(deps.recordingsInStyle.value))
    Object.values(deps.recordingsInStyle.value).forEach(
      (sentenceRecordingMetadataWithPitchedAudios: Array<SentenceRecordingMetadataWithPitchedAudios>) => {
        nRecordings += sentenceRecordingMetadataWithPitchedAudios.length
      },
    )

    // there are matches; no need for a warning box
    if (nRecordings) {
      return false
    }

    // no recordings and too many tags to choose from: warning box
    if (deps.choosableTags.value.length > 1) {
      return true
    }
    return false
  })
  // }}}

  // {{{ createMelodyWatcher
  type MelodyKey = 'torahMelody' | 'haftarahMelody' | 'sentenceGroupMelody'
  const ALL_MELODY_KEYS: MelodyKey[] = ['torahMelody', 'haftarahMelody', 'sentenceGroupMelody']

  /**
   * Factory for CHAIN 3: Melody mutual exclusivity watchers (watchers 5, 6, 7).
   * When the source content-type ref changes:
   *   - Sets the corresponding melody from store (or clears on deselect)
   *   - Clears the other two melody types (mutual exclusivity)
   *   - Optionally resets tikkunStyle
   *   - Always resets automaticallyFlippedVoiceStyle
   */
  function createMelodyWatcher(
    source: Ref<AliyahType | HaftarahType | SentenceGroupType | null>,
    label: string,
    melodyKey: MelodyKey,
    resetTikkun: boolean,
  ): void {
    const clearKeys = ALL_MELODY_KEYS.filter((k) => k !== melodyKey)
    watch(
      () => source.value,
      () => {
        logger.debug(`PracticeView watch state.${label}, state.${label} = ${JSON.stringify(source.value)}`)
        if (!deps.store.getters.student.barMitzvah) {
          logger.error(`unexpected null barMitzvah in watch.${label}`)
          return
        }

        if (!source.value) {
          deps.focusAnnotatedSentence.value = null
          ;(deps.voiceStyleSet.value as Record<string, unknown>)[melodyKey] = null
        } else {
          ;(deps.voiceStyleSet.value as Record<string, unknown>)[melodyKey] = (
            deps.store.getters.student.barMitzvah as Record<string, unknown>
          )[melodyKey]
          for (const key of clearKeys) {
            ;(deps.voiceStyleSet.value as Record<string, unknown>)[key] = null
          }
        }
        if (resetTikkun) {
          deps.tikkunStyle.value = false
        }
        deps.automaticallyFlippedVoiceStyle.value = false
      },
    )
  }
  // }}}
  // {{{ createListenerInvokerWatcher
  /**
   * Factory for listener-invoker watchers (CHAIN 1 continuation, watchers 11, 12).
   * Creates a deep watcher that invokes all registered one-shot listeners
   * when the source computed ref recomputes.
   */
  function createListenerInvokerWatcher(source: ComputedRef<Array<TagType>>, listeners: Ref<Set<() => void>>, label: string): void {
    watch(
      source,
      () => {
        for (const listener of listeners.value) {
          logger.debug(`invoking listener on watch ${label}`)
          listener()
        }
      },
      { deep: true },
    )
  }
  // }}}

  // {{{ registerWatchers
  /**
   * Must be called from onMounted in the parent.
   * Registers all 15 style/tag watchers in their exact original order.
   * @sideeffect Registers global watch/watchEffect handlers
   */
  function registerWatchers() {
    // {{{ watcher 1: watch recordingsInStyle
    /**
     * CHAIN 6: Auto-flip voice style on load
     * @watches recordingsInStyle (deep)
     * @invariant When a section has only sung recordings (no read recordings), auto-switches to
     *   Sing mode exactly once. Manual changes after auto-flip are not overridden.
     * @precondition automaticallyFlippedVoiceStyle tracks whether we've already auto-flipped
     * @postcondition voiceStyleSet.voice may change to SING; automaticallyFlippedVoiceStyle set to true
     * @sideeffect Mutates deps.voiceStyleSet.voice (triggers Chain 2 watcher)
     * @async-note Uses nextTick to wait for choosableTags to settle before checking
     */
    watch(deps.recordingsInStyle, () => {
      // if someone chose TextMode, don't auto-flip to something else
      if (inTextMode(deps.voiceStyleSet.value)) {
        return
      }

      // Don't auto-flip before recordings have loaded — an empty recordingsInStyle
      // on initial mount is the unloaded state, not "no recordings in this style."
      // Only consider flipping when the store has at least some recordings loaded.
      if (!deps.store.getters.audio.hasRecordings) {
        return
      }

      nextTick(() => {
        // there are no recordings in the current style and no choosable tags, then switch to sing
        if (
          !Object.keys(deps.recordingsInStyle.value).length &&
          deps.choosableTags.value.length <= 1 &&
          !deps.automaticallyFlippedVoiceStyle.value
        ) {
          logger.debug(`watch recordingsInStyle' setting .voice = ${VoiceStyles[SING]}`)
          deps.voiceStyleSet.value.voice = VoiceStyles[SING]
          deps.automaticallyFlippedVoiceStyle.value = true
        }
      })
    })
    // }}}
    // {{{ watcher 2: watch sentence
    /**
     * @watches sentence (deep) — the computed current focus sentence
     * @invariant manualSegmentJump is always false when sentence is responsive
     * @postcondition If sentence.isResponsive, manualSegmentJump forced to false
     * @sideeffect Mutates deps.manualSegmentJump
     */
    watch(
      deps.sentence,
      () => {
        if (deps.sentence.value && deps.sentence.value.isResponsive) {
          deps.manualSegmentJump.value = false
        }
      },
      { deep: true },
    )
    // }}}
    // {{{ watcher 3: watch [aliyah, haftarah, sentenceGroup, sentences]
    /**
     * @watches [deps.aliyah, deps.haftarah, deps.sentenceGroup, deps.sentences] (deep)
     * @invariant transformation is always consistent with current content type and voice mode
     * @postcondition autoSetTransformation() called — may switch transformation
     * @sideeffect Mutates deps.transformation via autoSetTransformation()
     */
    watch(
      () => [deps.aliyah.value, deps.haftarah.value, deps.sentenceGroup.value, deps.sentences.value],
      () => {
        logger.debug('PracticeView watch state.practice-select')
        autoSetTransformation()
      },
      { deep: true },
    )
    // }}}
    // {{{ watcher 4: watch state.sentences
    /**
     * CHAIN 1: Tag preservation across sentence changes
     * @watches deps.sentences
     * @invariant Selected tags survive sentence changes if the tag still exists in the new
     *   choosable set. Tags are always cleared synchronously, then restored asynchronously
     *   via the choosableHiddenTagsListeners one-shot listener pattern.
     * @precondition choosableHiddenTagsListeners Set exists and is watched by watcher #11
     * @postcondition Old sentences released from audio store memory; selectedHiddenTag and
     *   selectedNonHiddenTags cleared immediately; a listener registered for async restoration
     * @sideeffect store.dispatch.audio.releaseSentence for out-of-scope sentences
     * @sideeffect Clears selectedHiddenTag, selectedNonHiddenTags, automaticallyFlippedVoiceStyle
     * @sideeffect Calls closePracticeOverlay()
     * @async-note Tag restoration is deferred: runs when choosableHiddenTags recomputes
     *   (triggered by watcher #11). The listener self-removes after executing.
     */
    watch(
      () => deps.sentences.value,
      (newSentences: Array<SentenceType>, oldSentences: Array<SentenceType>): void => {
        // any sentences that are going out of scope can be released from memory
        // NB: duplicate of Recordings.watch.sentences
        const sentencesOutOfScope = oldSentences.filter((s) => !newSentences.includes(s))
        sentencesOutOfScope.forEach((sentence: SentenceType) => {
          deps.store.dispatch.audio.releaseSentence({ sentence })
        })

        // restore the currently selected tags after a while, if they're still in
        // the choosableTags set; note that we're handling both hiddenTags and nonHiddenUserTags
        // in the same callback and silently assume that
        // choosableHiddenTagsListeners and choosableNonHiddenTagsListeners would
        // call at the same time
        if (oldSentences.length) {
          // Save only tag names — avoids structuredClone on reactive GraphQL objects
          const savedTagNames = (deps.voiceStyleSet.value.tags ?? []).map((t) => t.name)
          const restoreTagsListener = () => {
            savedTagNames.forEach((name: string) => {
              const foundTag = findTagByName(deps.choosableTags.value, name)

              if (foundTag) {
                if (foundTag.hidden) {
                  deps.selectedHiddenTag.value = foundTag
                } else {
                  deps.selectedNonHiddenTags.value.push(foundTag)
                }
              }
            })
            deps.choosableHiddenTagsListeners.value.delete(restoreTagsListener)
          }

          deps.choosableHiddenTagsListeners.value.add(restoreTagsListener)
        }

        logger.debug('watch state.sentences, clearing selectedHiddenTag and selectedNonHiddenTags')
        deps.selectedHiddenTag.value = null
        deps.selectedNonHiddenTags.value = []
        deps.automaticallyFlippedVoiceStyle.value = false
        deps.closePracticeOverlay()
      },
    )
    // }}}
    // {{{ watchers 5, 6, 7: CHAIN 3 — Melody mutual exclusivity
    /**
     * @watches deps.aliyah, deps.haftarah, deps.sentenceGroup (one watcher each)
     * @invariant Exactly one melody type is non-null at a time. When a content type is selected,
     *   its melody is set from store and the other two are cleared.
     * @precondition store.getters.student.barMitzvah must exist
     * @sideeffect Mutates voiceStyleSet melody properties, tikkunStyle, automaticallyFlippedVoiceStyle
     * @sideeffect Clears focusAnnotatedSentence when content deselected
     */
    createMelodyWatcher(deps.aliyah, 'aliyah', 'torahMelody', false)
    createMelodyWatcher(deps.haftarah, 'haftarah', 'haftarahMelody', true)
    createMelodyWatcher(deps.sentenceGroup, 'sentenceGroup', 'sentenceGroupMelody', true)
    // }}}
    // {{{ watcher 8: watch store.getters.student.barMitzvah
    /**
     * @watches store.getters.student.barMitzvah (deep)
     * @invariant voiceStyleSet.pronunciation always matches the bar mitzvah's pronunciation
     * @postcondition voiceStyleSet.pronunciation set from barMitzvah
     * @sideeffect Mutates voiceStyleSet.pronunciation
     * Note: melodies are NOT set here — they are managed by Chain 3 watchers (aliyah/haftarah/sentenceGroup)
     */
    watch(
      () => deps.store.getters.student.barMitzvah,
      () => {
        if (!deps.store.getters.student.barMitzvah) {
          return
        }
        deps.voiceStyleSet.value.pronunciation = deps.store.getters.student.barMitzvah.pronunciation as PronunciationType
        // haftarahMelody and torahMelody and sentenceGroupMelody are set in aliyah/haftarah/sentenceGroup watchers
      },
      { deep: true },
    )
    // }}}
    // NOTE: watch(state.surroundLines) stays in PracticeView — it's navigation-related.
    // Its position between watcher #8 and #9 doesn't affect correctness because it watches
    // a different source (surroundLines) than any of the style/tag watchers.
    // {{{ watcher 9: watchEffect recompute voiceStyleSet.tags
    /**
     * @watches (watchEffect) deps.selectedHiddenTag, deps.selectedNonHiddenTags
     * @invariant voiceStyleSet.tags is always the concatenation of selectedHiddenTag (if any) + selectedNonHiddenTags
     * @postcondition voiceStyleSet.tags reflects current tag selections
     * @sideeffect Mutates voiceStyleSet.tags (triggers Chain 2 watcher: watch state.voiceStyleSet)
     */
    watchEffect(() => {
      deps.voiceStyleSet.value.tags = []
      if (deps.selectedHiddenTag.value) {
        deps.voiceStyleSet.value.tags.push(deps.selectedHiddenTag.value)
      }
      if (deps.selectedNonHiddenTags.value.length) {
        deps.voiceStyleSet.value.tags = deps.voiceStyleSet.value.tags.concat(deps.selectedNonHiddenTags.value)
      }
    })
    // }}}
    // {{{ watcher 10: watch state.voiceStyleSet
    /**
     * CHAIN 2: Voice style cascade
     * @watches deps.voiceStyleSet (deep)
     * @invariant Changing voice style never leaves the UI in an inconsistent state:
     *   - Active recording is stopped
     *   - Focused sentence is reloaded with new style or overlay is closed
     *   - Transformation is adjusted to match new voice mode
     * @precondition focusAnnotatedSentence and focusSentenceIndex are in sync
     * @postcondition Recording stopped if active; overlay updated or closed; transformation synced
     * @sideeffect Calls toggleRecord() if recording
     * @sideeffect Calls handleSentenceClick() or closePracticeOverlay()
     * @sideeffect Calls autoSetTransformation()
     */
    watch(
      () => deps.voiceStyleSet.value,
      () => {
        logger.debug(`PracticeView watch state.voiceStyleSet, state.voiceStyleSet = ${JSON.stringify(deps.voiceStyleSet.value)}`)
        if (deps.isRecording.value) {
          _toggleRecord()
        }

        if (
          deps.focusAnnotatedSentence.value &&
          deps.hasRecordingByAnnotatedSentence(deps.focusAnnotatedSentence.value as AnnotatedSentence)
        ) {
          deps.handleSentenceClick(deps.focusAnnotatedSentence.value as AnnotatedSentence, deps.focusSentenceIndex.value)
        } else {
          deps.closePracticeOverlay()
        }
        autoSetTransformation()

        // // trigger a computed()
        // if (_.isEmpty(recordingsInStyle.value)) {
        //   // nop
        // }
      },
      { deep: true },
    )
    // }}}
    // {{{ watchers 11, 12: CHAIN 1 continuation — invoke deferred tag listeners
    /**
     * @watches choosableHiddenTags, choosableNonHiddenTags (deep, one watcher each)
     * @invariant All one-shot listeners in the corresponding Set are invoked when the
     *   computed ref recomputes. Listeners self-remove after execution.
     * @precondition Listeners registered by watch(state.sentences) Chain 1 or restoreStateFromPracticeSettings Chain 4
     * @sideeffect Executes listener callbacks (typically restoreTags or setTagsFromURL)
     */
    createListenerInvokerWatcher(deps.choosableHiddenTags, deps.choosableHiddenTagsListeners, 'choosableHiddenTags')
    createListenerInvokerWatcher(deps.choosableNonHiddenTags, deps.choosableNonHiddenTagsListeners, 'choosableNonHiddenTags')
    // }}}
    // {{{ watcher 13: watchEffect choosableNonHiddenTags -> selectedNonHiddenTags
    /**
     * @watches (watchEffect) choosableNonHiddenTags.value, deps.selectedNonHiddenTags
     * @invariant selectedNonHiddenTags only contains tags that exist in choosableNonHiddenTags
     * @postcondition Any selected tags not in the current choosable set are removed
     * @sideeffect Mutates deps.selectedNonHiddenTags (triggers tag aggregation watchEffect above)
     */
    watchEffect(() => {
      // remove any tags that are currently selected that are no longer in the set of choosableNonHiddenTags
      deps.selectedNonHiddenTags.value = deps.selectedNonHiddenTags.value.filter(
        (selectedTag: TagType | null) => selectedTag && findTagByName(deps.choosableNonHiddenTags.value, selectedTag.name),
      )
    })
    // }}}
    // {{{ watcher 14: watchEffect choosableHiddenTags -> selectedHiddenTag
    /**
     * @watches (watchEffect) choosableHiddenTags.value, deps.selectedHiddenTag
     * @invariant selectedHiddenTag is always null or a member of choosableHiddenTags.
     *   If only one hidden tag is choosable, it is auto-selected.
     * @postcondition selectedHiddenTag cleared if not in choosable set; auto-selected if only one option
     * @sideeffect Mutates deps.selectedHiddenTag (triggers tag aggregation watchEffect)
     */
    watchEffect(() => {
      if (!deps.selectedHiddenTag.value) {
        return
      }

      const foundTag = findTagByName(deps.choosableHiddenTags.value, deps.selectedHiddenTag.value!.name)
      if (!foundTag) {
        deps.selectedHiddenTag.value = null
      } else if (deps.choosableHiddenTags.value.length === 1) {
        deps.selectedHiddenTag.value = deps.choosableHiddenTags.value[0]
      }
    })
    // }}}
    // {{{ watcher 15: watch state.selectedBarMitzvah
    /**
     * @watches deps.selectedBarMitzvah (deep, immediate)
     * @invariant When selectedBarMitzvah changes, all content selections are cleared and
     *   store.student.barMitzvah is updated to match
     * @postcondition aliyah/haftarah/sentenceGroup/sentences cleared on change; barMitzvah set in store
     * @sideeffect store.dispatch.student.clearBarMitzvah, store.dispatch.student.setBarMitzvah
     * @sideeffect Clears deps.aliyah, deps.haftarah, deps.sentenceGroup, deps.sentences
     * @async-note Uses nextTick before dispatching setBarMitzvah to let clearing propagate
     */
    watch(
      () => deps.selectedBarMitzvah.value,
      (newVal, oldVal) => {
        logger.debug('PracticeView, watch state.selectedBarMitzvah =')
        logger.debug(deps.selectedBarMitzvah.value)

        if (!newVal || oldVal !== newVal) {
          deps.store.dispatch.student.clearBarMitzvah()
          deps.aliyah.value = null
          deps.haftarah.value = null
          deps.sentenceGroup.value = null
          deps.sentences.value = []
        }

        if (deps.selectedBarMitzvah.value) {
          nextTick(() => {
            deps.store.dispatch.student.setBarMitzvah(deps.selectedBarMitzvah.value as BarMitzvahType)
          })
        }
      },
      { deep: true, immediate: true },
    )
    // }}}
  }
  // }}}

  return {
    autoSetTransformation,
    hiddenTagsStats,
    isInSingMode,
    isInTextMode,
    registerWatchers,
    relevantTransformations,
    setToggleRecord,
    tagWarning,
    tagWarningBox,
  }
}
